/**
 * ARRIVALS: events, bounce-backs, arrivals, diversion, specials,
 * interrupts and prompts (DESIGN §5.1, §6.13, §6.15).
 */
import { diag } from '../content/compendium.ts';
import { SPECIAL_FRONTS } from '../content/families.ts';
import { eventCard } from '../content/events.ts';
import { PRESETS } from '../content/presets.ts';
import { buildSpecial } from '../deck.ts';
import { hashSeed, Rng } from '../prng.ts';
import type { ActiveEvent, Patient, PatientId, Role, SessionState, TableState } from '../types.ts';
import { CONFIRMED, ROLE_ORDER } from '../types.ts';
import { ventsFree } from './act.ts';
import { clockLength, colourUp, emit, freeBedIndex, freeBeds, listOf, nextId, place, roleTitle, seatedRoles, type Ctx } from './common.ts';
import { addTicks, normaliseRoute, setColour } from './outcomes.ts';
import { applyE17 } from './team.ts';

const REST = 999;

/** How long each event stays "active" (inclusive last turn), relative to the turn it fires. */
function durationUntil(key: string, turn: number): number {
  switch (key) {
    case 'E1': case 'E5': case 'E8': case 'E10': case 'E24': case 'E36': case 'E39':
      return turn + 1;
    case 'E42': case 'E45':
      return turn + 2;
    case 'E7': case 'E40':
      return REST;
    default:
      return turn;
  }
}

function interrupt(ctx: Ctx, t: TableState, seat: Role, event: string, title: string, bonus = false, patient?: PatientId): void {
  if (!t.seats[seat]) return;
  t.interrupts.push({ id: nextId(ctx.s, 'interrupt'), seat, event, title, state: 'NEW', age: 0, dealtTurn: ctx.s.clock.turn, bonus, ...(patient ? { patient } : {}) });
  emit(ctx, { type: 'INTERRUPT', table: t.no, seat, severity: 1, tag: 'Interruptions', text: `Interruption for the ${roleTitle(seat)}: ${title}.`, audience: ['faculty', `seat:${seat}`] });
}

/** Put a drawn or special patient into a table's Emergency (bay, else corridor). */
export function arriveAtEmergency(ctx: Ctx, t: TableState, p: Patient): void {
  p.status = 'ACTIVE';
  p.arrivedTurn = ctx.s.clock.turn;
  p.table = t.no;
  const back = ctx.s.backs[p.id];
  if (back) back.drawn = true;
  if (t.effects.clusterLeft > 0 && !p.flags.bouncedBack) {
    t.effects.clusterLeft--;
    if (p.box !== CONFIRMED && p.box < 90) {
      p.flags.cluster = true;
      p.flags.clusterTrueBox = p.box;
      p.box = 90;
      if (back?.trap) back.bias = ['Availability', 'Three in an hour made this one look the same as the others.'];
    }
  }
  const bed = freeBedIndex(ctx.s, t, 'EM');
  place(ctx.s, t, 'EM', p, bed >= 0 ? bed : 'corridor');
  emit(ctx, { type: 'ARRIVED', table: t.no, seat: 'EM', patient: p.id, text: `${p.name} arrived in ${bed >= 0 ? `Bay ${bed + 1}` : 'the corridor'}.`, audience: ['faculty', 'seat:EM'] });
}

/** Most diverted arrivals one table takes in a turn, from all senders together (D-44). */
export const DIVERT_CAP = 2;

/** Rank real receiving capacity, rotating equal-capacity priority each turn. */
function divertTarget(s: SessionState, from: number, taken: Map<number, number>): TableState | null {
  const ring = s.config.ringOrder.length === s.tables.length ? s.config.ringOrder : s.tables.map((t) => t.no);
  const i = ring.indexOf(from);
  const candidates = [...ring.slice(i + 1), ...ring.slice(0, i)];
  const offset = (s.clock.turn - 1) % candidates.length;
  let best: TableState | null = null;
  let capacity = 0;
  for (let k = 0; k < candidates.length; k++) {
    const t = s.tables.find((x) => x.no === candidates[(k + offset) % candidates.length]);
    if (!t || t.board.divert || (taken.get(t.no) ?? 0) >= DIVERT_CAP) continue;
    const free = freeBeds(s, t, 'EM').length;
    if (free <= capacity) continue;
    best = t;
    capacity = free;
  }
  return best;
}

function drawFromDeck(ctx: Ctx, t: TableState): Patient | null {
  const id = t.deck.shift();
  if (!id) {
    emit(ctx, { type: 'DECK_EMPTY', table: t.no, text: `Table ${t.no}'s deck is empty.` });
    return null;
  }
  return ctx.s.patients[id]!;
}

/** Apply patient-facing events at release. */
function applyPatientEvent(ctx: Ctx, t: TableState, key: string, extras: Map<number, PatientId[]>, arrivalOverride: Map<number, number>): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const add = (id: PatientId) => extras.set(t.no, [...(extras.get(t.no) ?? []), id]);
  switch (key) {
    case 'E6':
      for (const n of SPECIAL_FRONTS.sprocket.names) add(buildSpecial(s, ctx.rng, t.no, t.fiveSeat, 'sprocket', { name: n }));
      break;
    case 'E12':
      arrivalOverride.set(t.no, 4);
      break;
    case 'E13': {
      const rng = new Rng(hashSeed(`${s.config.seed}:event:${turn}:${t.no}:E13`));
      const odd = rng.int(4);
      SPECIAL_FRONTS.choir.names.forEach((n, i) => add(buildSpecial(s, rng, t.no, t.fiveSeat, 'choir', { name: n, truth: i === odd ? 'silent-stroke' : undefined })));
      break;
    }
    case 'E14':
      add(buildSpecial(s, ctx.rng, t.no, t.fiveSeat, 'vip'));
      interrupt(ctx, t, t.lead, key, 'The Mayor’s Cousin wants the person in charge');
      break;
    case 'E16':
      for (const p of Object.values(s.patients).filter(p => p.table === t.no && p.status === 'ACTIVE' && s.backs[p.id]?.truthKey === 'lycanthropy')) addTicks(p, 1);
      break;
    case 'E18':
      t.effects.clusterLeft = 3;
      break;
    case 'E21':
      for (const p of ROLE_ORDER.flatMap((r) => listOf(s, t, r)).filter(p => p.referral)) {
        addTicks(p, 1);
        emit(ctx, { type: 'LOST_IN_TRANSFER', table: t.no, seat: p.seat, patient: p.id, severity: 1, tag: 'Closed-loop handover', text: `${p.name} was referred and nothing has happened since: waiting time +1.`, audience: ['faculty', `seat:${p.seat!}`] });
      }
      break;
    case 'E33':
      if (t.callsForHelp > 0) t.locums.push({ id: `L${t.locums.length + 1}`, arrivesTurn: turn, untilTurn: turn + 1, actionsLeft: 2 });
      break;
  }
}

/** Keep event decisions open until the action plans commit. */
function applyDecisionEvent(ctx: Ctx, t: TableState, key: string): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  switch (key) {
    case 'E17':
      if (t.seats[t.lead]) t.prompts.push({ id: nextId(s, 'prompt'), seat: t.lead, kind: 'E17', options: ['COMPLY', 'CHALLENGE'], phase: 'ACT' });
      break;
    case 'E23':
      if (t.seats[t.lead]) t.prompts.push({ id: nextId(s, 'prompt'), seat: t.lead, kind: 'E23', options: seatedRoles(t).filter((r) => r !== t.lead), phase: 'ACT' });
      break;
    case 'E24':
      if (t.seats.PHYS) {
        t.effects.physicianAwayUntil = turn + 1;
        t.effects.cover = null;
        t.prompts.push({ id: nextId(s, 'prompt'), seat: 'PHYS', kind: 'E24', options: seatedRoles(t).filter((r) => r !== 'PHYS'), phase: 'ACT' });
      }
      break;
  }
}

/** Give workload events an explicit interrupt or action consequence. */
function applyWorkloadEvent(ctx: Ctx, t: TableState, key: string): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  switch (key) {
    case 'E20':
      interrupt(ctx, t, t.lead, key, 'Report patient and bed numbers, and how many were saved, harmed and died');
      break;
    case 'E27':
      interrupt(ctx, t, 'EM', key, 'Complaint at the desk');
      break;
    case 'E28':
      interrupt(ctx, t, 'PHYS', key, 'Lost notes');
      break;
    case 'E29': {
      const best = seatedRoles(t).sort((a, b) => listOf(s, t, b).length - listOf(s, t, a).length)[0];
      if (best) interrupt(ctx, t, best, key, 'Lab phone: critical result', true);
      break;
    }
    case 'E44': {
      const worst = ROLE_ORDER.flatMap(r => listOf(s, t, r)).sort((a, b) => b.ticks / clockLength(b.colour) - a.ticks / clockLength(a.colour))[0];
      if (worst) interrupt(ctx, t, t.lead, key, `Ryan’s Rule: the family of ${worst.name} want a review`, false, worst.id);
      break;
    }
    case 'E45': {
      const fewest = seatedRoles(t).sort((a, b) => listOf(s, t, a).length - listOf(s, t, b).length)[0];
      if (fewest) {
        t.effects.sickLeave = { role: fewest, until: turn + 2 };
        emit(ctx, { type: 'SICK_LEAVE', table: t.no, seat: fewest, severity: 1, tag: 'Redistribute workload', text: `${roleTitle(fewest)} goes home unwell: one fewer action for three turns.`, audience: ['faculty', 'table'] });
      }
      break;
    }
  }
}

/** Apply changes only to the enabled resource layer. */
function applyLayerEvent(ctx: Ctx, t: TableState, key: string): void {
  const turn = ctx.s.clock.turn;
  switch (key) {
    case 'E36': {
      const best = seatedRoles(t).sort((a, b) => t.seats[b]!.staff - t.seats[a]!.staff)[0];
      if (best) {
        const n = Math.min(2, t.seats[best]!.staff);
        t.seats[best]!.staff -= n;
        t.board.staffAdjust.push({ role: best, n, until: turn + 1 });
      }
      break;
    }
    case 'E37':
      t.seats[t.lead]!.staff += 2;
      break;
    case 'E38':
      for (const r of seatedRoles(t).filter(r => t.seats[r]!.staff > 0)) {
        t.seats[r]!.staff -= 1;
        t.board.staffAdjust.push({ role: r, n: 1, until: turn });
      }
      break;
    case 'E39':
      if (t.board.vents) {
        t.board.vents.recalled = 1;
        t.board.vents.recalledUntil = turn + 1;
      }
      break;
    case 'E40':
      if (t.board.blood) t.board.blood.units = Math.max(0, t.board.blood.units - 2);
      break;
    case 'E41':
      if (t.board.blood) {
        const before = t.board.blood.units;
        t.board.blood.units = Math.min(12, before + 4);
        t.board.blood.wasted += before + 4 - t.board.blood.units;
      }
      break;
  }
}

/** Admit a regional retrieval only when an ICU bed exists. */
function arriveRegional(ctx: Ctx, t: TableState): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const bed = freeBedIndex(s, t, 'ICU');
  if (bed < 0) {
    emit(ctx, { type: 'REGIONAL_NO_BED', table: t.no, text: `Table ${t.no} has no free ICU bed for the retrieval.`, audience: ['faculty', 'table'] });
    return;
  }
  const rng = new Rng(hashSeed(`${s.config.seed}:event:${turn}:${t.no}:E34`));
  const key2 = SPECIAL_FRONTS.regional[rng.int(SPECIAL_FRONTS.regional.length)]!;
  const id = buildSpecial(s, rng, t.no, t.fiveSeat, 'regional', { key: key2, name: `Retrieval patient (${diag(key2).name})` });
  const p = s.patients[id]!;
  p.status = 'ACTIVE';
  p.arrivedTurn = turn;
  p.ticks = 1;
  p.needsStabilise = false;
  p.route = { code: diag(key2).routes.find((r) => r.steps.some((x) => x.kind === 'ICU'))?.code ?? 'M', next: 0, startedTurn: turn, diag: key2 };
  normaliseRoute(p);
  place(s, t, 'ICU', p, bed);
  if (t.board.vents && diag(key2).vent && ventsFree(s, t) > 0) p.flags.ventHeld = true;
  t.dashboard.accepted++;
  emit(ctx, { type: 'REGIONAL_RED', table: t.no, seat: 'ICU', patient: id, severity: 2, tag: 'System thinking', text: `A retrieval patient went straight into Table ${t.no}'s ICU. Support is due this turn.`, audience: ['faculty', 'seat:ICU'] });
}

/** Release scheduled locums and blood before this turn opens. */
function releaseSupplies(ctx: Ctx, t: TableState): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  for (const l of t.locums.filter(l => l.arrivesTurn === turn)) {
    l.actionsLeft = 2;
    emit(ctx, { type: 'LOCUM_ARRIVED', table: t.no, severity: 1, text: `A Locum has arrived at Table ${t.no}.`, audience: ['faculty', 'table'] });
  }
  if (t.board.blood) {
    const due = t.board.blood.orders.filter((o) => o.arriveTurn <= turn);
    for (const o of due) {
      const before = t.board.blood.units;
      t.board.blood.units = Math.min(12, before + o.units);
      t.board.blood.wasted += before + o.units - t.board.blood.units;
    }
    t.board.blood.orders = t.board.blood.orders.filter((o) => o.arriveTurn > turn);
  }
}

/** Return due bounce-backs through the normal arrival path. */
function returnPatients(ctx: Ctx, t: TableState): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const back = t.bounceSlot.filter((b) => b.returnTurn === turn);
  t.bounceSlot = t.bounceSlot.filter((b) => b.returnTurn !== turn);
  for (const b of back) {
    const p = s.patients[b.id]!;
    setColour(p, colourUp(p.colour));
    p.box = CONFIRMED;
    p.flags.bouncedBack = true;
    p.flags.cluster = false;
    if (p.colour === 'RED') p.needsStabilise = true;
    t.dashboard.harmed++;
    t.dashboard.bounceBacks++;
    arriveAtEmergency(ctx, t, p);
    emit(ctx, { type: 'BOUNCED_BACK', table: t.no, seat: 'EM', patient: p.id, severity: 2, tag: 'Premature closure; flow', text: `${p.name} has bounced back, one colour worse.`, audience: ['faculty', 'seat:EM'] });
  }
}

/** Route this turn's arrivals against actual receiving capacity. */
function deliverArrivals(ctx: Ctx, t: TableState, n: number, taken: Map<number, number>): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const diverting = t.board.divert && PRESETS[s.config.preset].divert && s.tables.length >= 3;
  const sentTo = new Map<number, number>();
  let home = 0;
  for (let i = 0; i < n; i++) {
    const p = drawFromDeck(ctx, t);
    if (!p) break;
    const to = (diverting ? divertTarget(s, t.no, taken) : null) ?? t;
    if (to.no !== t.no) {
      p.flags.divertedFrom = t.no;
      t.dashboard.diverted++;
      to.dashboard.accepted++;
      taken.set(to.no, (taken.get(to.no) ?? 0) + 1);
      sentTo.set(to.no, (sentTo.get(to.no) ?? 0) + 1);
    } else if (diverting) home++;
    arriveAtEmergency(ctx, to, p);
  }
  if (sentTo.size > 0) t.tableStats.divertTurns++;
  for (const [no, count] of sentTo) {
    s.tables.find((x) => x.no === no)!.divertNotice.push({ fromTable: t.no, count, turn });
    emit(ctx, { type: 'DIVERTED', table: t.no, severity: 2, tag: 'System thinking', text: `Table ${t.no} diverted ${count} arrival${count > 1 ? 's' : ''} to Table ${no}.`, audience: ['faculty', 'table'], data: { to: no, n: count } });
  }
  if (home > 0) emit(ctx, { type: 'DIVERT_FULL', table: t.no, severity: 2, tag: 'System thinking', text: `Table ${t.no} is on divert but nobody can take ${home === 1 ? 'one arrival' : `${home} arrivals`}: they stay.`, audience: ['faculty', 'table'], data: { n: home } });
}

// ───────────────────────────── event application ─────────────────────────────

/** Apply one event's immediate effects to every table. Returns extra special arrivals per table. */
export function applyEvent(ctx: Ctx, key: string, extras: Map<number, PatientId[]>, arrivalOverride: Map<number, number>): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const card = eventCard(key);
  if (card.layer && !s.config.layers[card.layer]) {
    emit(ctx, { type: 'EVENT_SKIPPED', text: `${key} skipped: the ${card.layer} layer is off.` });
    return;
  }
  if (card.regional && !PRESETS[s.config.preset].divert && key === 'E34') return;
  const ae: ActiveEvent = { key, turn, until: durationUntil(key, turn) };
  s.room.activeEvents.push(ae);
  s.room.thisTurnEvents.push(key);
  emit(ctx, { type: 'EVENT', severity: 1, tag: card.tag, text: `${key} ${card.title}: ${card.rule}`, audience: ['faculty', 'table'], data: { key } });

  for (const t of s.tables) {
    applyPatientEvent(ctx, t, key, extras, arrivalOverride);
    applyDecisionEvent(ctx, t, key);
    applyWorkloadEvent(ctx, t, key);
    applyLayerEvent(ctx, t, key);
    if (key === 'E34') arriveRegional(ctx, t);
  }
}

// ───────────────────────────── ARRIVALS entry ─────────────────────────────

export function enterArrivals(ctx: Ctx): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  s.room.thisTurnEvents = [];
  // patients on lists at the start of the turn (objective: Early finish)
  for (const t of s.tables) for (const r of seatedRoles(t)) t.seats[r]!.stats.patientsAtStartOfTurn[turn] = listOf(s, t, r).length;

  let row = s.script[turn - 1]!;
  const events = [...row.events, ...s.room.queuedInjects];
  s.room.queuedInjects = [];

  // E35: the last turn becomes a normal turn; the handover moves one later.
  if (events.includes('E35') && row.handover && !s.room.e35Applied) {
    s.room.e35Applied = true;
    row.handover = false;
    row.arrivals = 1;
    s.script.push({ gameTurn: turn + 1, scriptTurn: row.scriptTurn + 1, arrivals: 0, events: [], handover: true });
    s.clock.lastTurn = s.script.length;
    row = s.script[turn - 1]!;
  }

  const extras = new Map<number, PatientId[]>();
  const override = new Map<number, number>();
  for (const key of events) applyEvent(ctx, key, extras, override);

  for (const t of s.tables) {
    releaseSupplies(ctx, t);
    returnPatients(ctx, t);
  }

  const taken = new Map<number, number>();
  for (const t of s.tables) deliverArrivals(ctx, t, override.get(t.no) ?? row.arrivals, taken);

  // specials from events: never diverted
  for (const t of s.tables) {
    for (const id of extras.get(t.no) ?? []) arriveAtEmergency(ctx, t, s.patients[id]!);
  }


}

/** Apply unanswered handover and cover defaults at commitment. */
export function leaveArrivals(ctx: Ctx): void {
  const s = ctx.s;
  const decisions = s.tables.flatMap(t => t.prompts.filter(q => q.kind === 'E23' || q.kind === 'E24').map(q => ({ t, q })));
  for (const { t, q } of decisions) {
    if (q.kind === 'E23') {
      const ring = seatedRoles(t);
      const next = ring[(ring.indexOf(t.lead) + 1) % ring.length]!;
      t.effects.leadPenalty = { role: t.lead, turn: s.clock.turn + 1 };
      emit(ctx, { type: 'LEAD_PASSED', table: t.no, seat: next, severity: 1, tag: 'Leadership handover', text: `The Lead was called away without choosing: the Lead passed to ${roleTitle(next)}.`, audience: ['faculty', 'table'] });
      t.lead = next;
    }
    if (q.kind === 'E24') {
      t.effects.cover = t.seats.EM ? 'EM' : (seatedRoles(t).find((r) => r !== 'PHYS') ?? null);
      emit(ctx, { type: 'COVER', table: t.no, seat: t.effects.cover, severity: 1, tag: 'Cover; handover', text: `Nobody was named: ${t.effects.cover} covers the Physician's list.`, audience: ['faculty', 'table'] });
    }
  }
  for (const t of s.tables) t.prompts = t.prompts.filter((x) => x.kind !== 'E23' && x.kind !== 'E24');
}

/** Default an unanswered E17 choice to Comply at commitment. */
export function leaveHuddle(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    if (t.prompts.some((q) => q.kind === 'E17')) {
      t.prompts = t.prompts.filter((q) => q.kind !== 'E17');
      applyE17(ctx.s, t, 'COMPLY');
      emit(ctx, { type: 'E17_COMPLY', table: t.no, seat: t.lead, severity: 2, tag: 'Authority gradient', text: `Table ${t.no} said nothing: the Consultant’s decree stands.`, audience: ['faculty', 'table'] });
    }
  }
}
